import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import healthApi from '@/api/healthApi';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ArrowLeft, BookOpen, Sparkles, CheckCircle2, Target } from 'lucide-react';
import { getPersonalizedHealthContent, healthCategories, getContentById } from '@/utils/healthContentDatabase';

const HealthHub = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [personalizedContent, setPersonalizedContent] = useState([]);
  const [generalContent, setGeneralContent] = useState([]);
  const [allContent, setAllContent] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedContent, setSelectedContent] = useState(null);
  const [showContentModal, setShowContentModal] = useState(false);

  useEffect(() => {
    loadHealthContent();
  }, [user]);

  const loadHealthContent = async () => {
    try {
      setLoading(true);
      
      // Fetch user data for personalization
      const { data: trendsData } = await healthApi.getTrends();
      const recentCheckins = trendsData?.recent_checkins || [];
      
      // Get medications if available
      let medications = [];
      try {
        const { data: medsData } = await healthApi.getMedications();
        medications = medsData || [];
      } catch (e) {
        // No medications or error - that's okay
      }
      
      // Get personalized recommendations
      const { personalized, general, allContent: all } = getPersonalizedHealthContent(
        user,
        recentCheckins,
        medications
      );
      
      setPersonalizedContent(personalized);
      setGeneralContent(general);
      setAllContent(all);
    } catch (error) {
      console.error('Error loading health content:', error);
      // Fallback to non-personalized
      const { general, allContent: all } = getPersonalizedHealthContent(user, [], []);
      setGeneralContent(general);
      setAllContent(all);
    } finally {
      setLoading(false);
    }
  };

  const handleContentClick = (contentId) => {
    const content = getContentById(contentId);
    setSelectedContent(content);
    setShowContentModal(true);
  };

  const getCategoryColor = (category) => {
    const cat = healthCategories.find(c => c.id === category);
    return cat?.color || 'gray';
  };

  const filteredContent = selectedCategory === 'all' 
    ? allContent 
    : allContent.filter(c => c.category === selectedCategory);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard')}
                className="mr-2"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <BookOpen className="h-7 w-7 text-purple-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Health Hub</h1>
                <p className="text-sm text-gray-600">Your personalized health knowledge center</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Personalized Recommendations */}
        {personalizedContent.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="h-5 w-5 text-purple-600" />
              <h2 className="text-xl font-bold text-gray-900">Recommended for You</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {personalizedContent.map((content) => {
                const color = getCategoryColor(content.category);
                return (
                  <Card
                    key={content.id}
                    className="cursor-pointer hover:shadow-lg transition-all border-2 border-purple-200 bg-purple-50"
                    onClick={() => handleContentClick(content.id)}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <span className="text-3xl mb-2">{content.icon}</span>
                        <Badge className="bg-purple-600 text-white">
                          For you
                        </Badge>
                      </div>
                      <CardTitle className="text-base">{content.title}</CardTitle>
                      <CardDescription className="text-xs">{content.summary}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between text-xs text-gray-600">
                        <span>{content.readTime} read</span>
                        <span className="capitalize">{content.category.replace('_', ' ')}</span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Category Filter */}
        <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-2">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
              selectedCategory === 'all'
                ? 'bg-purple-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50 border'
            }`}
          >
            All Topics
          </button>
          {healthCategories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap ${
                selectedCategory === cat.id
                  ? `bg-${cat.color}-600 text-white`
                  : 'bg-white text-gray-700 hover:bg-gray-50 border'
              }`}
            >
              <span>{cat.icon}</span>
              <span>{cat.name}</span>
            </button>
          ))}
        </div>

        {/* All Content */}
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            {selectedCategory === 'all' ? 'All Content' : `${healthCategories.find(c => c.id === selectedCategory)?.name} Content`}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredContent.map((content) => (
              <Card
                key={content.id}
                className="cursor-pointer hover:shadow-lg transition-all"
                onClick={() => handleContentClick(content.id)}
              >
                <CardHeader className="pb-3">
                  <span className="text-3xl mb-2">{content.icon}</span>
                  <CardTitle className="text-base">{content.title}</CardTitle>
                  <CardDescription className="text-xs">{content.summary}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between text-xs text-gray-600">
                    <span>{content.readTime} read</span>
                    <span className="capitalize">{content.category.replace('_', ' ')}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Content Modal */}
      <Dialog open={showContentModal} onOpenChange={setShowContentModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedContent && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-4xl">{selectedContent.icon}</span>
                  <div>
                    <DialogTitle className="text-xl">{selectedContent.title}</DialogTitle>
                    <p className="text-sm text-gray-600 mt-1">{selectedContent.readTime} • {selectedContent.category.replace('_', ' ')}</p>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-6 pt-4">
                {/* Overview */}
                <div>
                  <p className="text-gray-700 leading-relaxed">
                    {selectedContent.content.overview}
                  </p>
                </div>

                {/* Key Points */}
                <div>
                  <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                    Key Points
                  </h3>
                  <ul className="space-y-2">
                    {selectedContent.content.keyPoints.map((point, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                        <span className="text-purple-600 font-bold mt-1">•</span>
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* What You Can Do Today */}
                <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-4">
                  <h3 className="text-base font-semibold text-purple-900 mb-3 flex items-center gap-2">
                    <Target className="h-5 w-5 text-purple-600" />
                    What You Can Do Today
                  </h3>
                  <ul className="space-y-2">
                    {selectedContent.content.whatYouCanDoToday.map((action, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm text-purple-900">
                        <span className="text-purple-600 font-bold mt-1">✓</span>
                        <span className="font-medium">{action}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <Button
                  onClick={() => setShowContentModal(false)}
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                >
                  Got it!
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HealthHub;
